/**
 * Centralized API Client for ShieldShot Frontend
 * Handles all API interactions with backend services
 */

import { API_URL } from '../../config';

/**
 * API Response wrapper for consistent error handling
 */
class ApiResponse {
  constructor(success, data = null, message = '', status = null) {
    this.success = success;
    this.data = data;
    this.message = message;
    this.status = status;
  }

  static success(data, message = 'Success') {
    return new ApiResponse(true, data, message, 200);
  }

  static error(message, status = null, data = null) {
    return new ApiResponse(false, data, message, status);
  }
}

/**
 * Enhanced fetch wrapper with standardized error handling and response parsing
 */
class ApiClient {
  constructor(baseURL = API_URL) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get authentication token - supports both string tokens and functions
   */
  async resolveToken(token) {
    if (typeof token === 'function') {
      try {
        return await token();
      } catch (error) {
        throw new Error('Failed to retrieve authentication token');
      }
    }
    
    if (!token || typeof token !== 'string' || token === 'undefined') {
      throw new Error('Invalid or missing authentication token');
    }
    
    return token;
  }

  /**
   * Build headers with authentication
   */
  async buildHeaders(token, additionalHeaders = {}) {
    // Public endpoints pass null/undefined — skip Authorization header
    if (token === null || token === undefined) {
      return {
        ...this.defaultHeaders,
        ...additionalHeaders,
      };
    }
    const resolvedToken = await this.resolveToken(token);
    return {
      ...this.defaultHeaders,
      Authorization: `Bearer ${resolvedToken}`,
      ...additionalHeaders,
    };
  }

  /**
   * Handle API response and errors consistently
   */
  async handleResponse(response) {
    const contentType = response.headers.get('content-type');
    let responseData;

    // Parse response based on content type
    if (contentType && contentType.includes('application/json')) {
      try {
        responseData = await response.json();
      } catch (error) {
        throw new Error('Invalid JSON response from server');
      }
    } else {
      responseData = await response.text();
    }

    // Unwrap backend envelopes ({success, data, message}) so components see
    // the actual payload, while preserving pass-through for non-enveloped data.
    const isEnvelope =
      typeof responseData === 'object' &&
      responseData !== null &&
      !Array.isArray(responseData) &&
      'success' in responseData;

    if (isEnvelope) {
      if (responseData.success === true) {
        return ApiResponse.success(responseData.data, responseData.message || 'Success');
      }
      // success === false (or missing) -> backend-level failure
      // Pass responseData.data if present, otherwise pass full responseData
      // so structured error codes (code, etc.) are accessible from apiResponse.data
      return ApiResponse.error(
        responseData.message || 'An unexpected error occurred',
        response.status,
        responseData.data || responseData
      );
    }

    // Handle successful responses (non-enveloped)
    if (response.ok) {
      return ApiResponse.success(responseData);
    }

    // Handle error responses with standardized messages
    let errorMessage = 'An unexpected error occurred';
    
    switch (response.status) {
      case 400:
        errorMessage = 'Bad request - please check your input';
        break;
      case 401:
        errorMessage = 'Authentication failed - please log in again';
        break;
      case 403:
        errorMessage = 'You are not authorized to access this resource';
        break;
      case 404:
        errorMessage = 'The requested resource was not found';
        break;
      case 410:
        errorMessage = 'This resource has expired';
        break;
      case 500:
        errorMessage = 'Internal server error - please try again later';
        break;
      case 502:
        errorMessage = 'The server rejected the request data';
        break;
      case 503:
        errorMessage = 'Service temporarily unavailable - please try again later';
        break;
      default:
        errorMessage = responseData?.message || `Server error (${response.status})`;
    }

    return ApiResponse.error(errorMessage, response.status, responseData);
  }

  /**
   * Generic request method with timeout and retry logic
   */
  async request(endpoint, options = {}) {
    const {
      method = 'GET',
      token,
      body,
      headers = {},
      timeout = 30000,
      retries = 1,
      retryDelay = 1000,
    } = options;

    // Build the full URL
    const url = `${this.baseURL}${endpoint}`;
    
    // Prepare request configuration
    const requestConfig = {
      method,
      headers: await this.buildHeaders(token, headers),
    };

    // Add body for non-GET requests
    if (body && method !== 'GET') {
      requestConfig.body = typeof body === 'string' ? body : JSON.stringify(body);
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    requestConfig.signal = controller.signal;

    let lastError;
    
    // Retry logic
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, requestConfig);
        clearTimeout(timeoutId);
        return await this.handleResponse(response);
      } catch (error) {
        lastError = error;
        
        if (error.name === 'AbortError') {
          clearTimeout(timeoutId);
          return ApiResponse.error('Request timeout - please try again', 408);
        }
        
        // Don't retry on the last attempt
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
        }
      }
    }

    clearTimeout(timeoutId);
    return ApiResponse.error(
      `Network error after ${retries + 1} attempts: ${lastError.message}`,
      null,
      lastError
    );
  }

  // Convenience methods for common HTTP verbs
  async get(endpoint, token, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET', token });
  }

  async post(endpoint, token, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', token, body });
  }

  async put(endpoint, token, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', token, body });
  }

  async delete(endpoint, token, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE', token });
  }

  async patch(endpoint, token, body = null, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', token, body });
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();
export { ApiResponse, ApiClient };
export default apiClient;
